<template>
    <div class="player">
        <div class="player__right-top">
            <button
                v-if="$isManageApp"
                class="button button--blank button--gray player__copy-src-button"
                @click.prevent="copySrc"
            >
                copy audio
            </button>
        </div>
        <div class="player__control">
            <slot name="extra-controls-before" />
            <button
                v-show="!playing"
                type="button"
                class="player__button player__button--play"
                @click.prevent="play"
            >
                <svg
                    width="20"
                    height="24"
                    viewBox="0 0 20 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M18.9189 10.2996L3.21078 0.237184C2.7464 -0.0439058 2.16977 0.00292533 1.73427 0.00292533C-0.00772719 0.00292533 5.97665e-07 1.37033 5.97665e-07 1.71676V22.2832C5.97665e-07 22.576 -0.00762557 23.9971 1.73427 23.9971C2.16977 23.9971 2.7465 24.0437 3.21078 23.7627L18.9188 13.7005C20.2081 12.9205 19.9853 12 19.9853 12C19.9853 12 20.2082 11.0795 18.9189 10.2996Z"
                        fill="#20d0a9"
                    />
                </svg>
            </button>
            <button
                v-show="playing"
                type="button"
                class="player__button player__button--pause"
                @click.prevent="pause"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 535.578 535.578"
                    width="20"
                    height="24"
                >
                    <path
                        d="M231.6 516.278c0 10.658-8.641 19.3-19.3 19.3H106.15c-10.659 0-19.3-8.641-19.3-19.3V19.3c0-10.659 8.641-19.3 19.3-19.3H212.3c10.659 0 19.3 8.641 19.3 19.3v496.978zM448.728 516.278c0 10.658-8.641 19.3-19.3 19.3h-106.15c-10.659 0-19.3-8.641-19.3-19.3V19.3c0-10.659 8.641-19.3 19.3-19.3h106.15c10.659 0 19.3 8.641 19.3 19.3v496.978z"
                        fill="#20d0a9"
                    />
                </svg>
            </button>
            <button
                v-if="rewindButton"
                class="player__rewind"
                type="button"
                @click.prevent="restart"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 489.533 489.533"
                    width="12"
                    height="12"
                >
                    <path
                        d="M268.175 488.161c98.2-11 176.9-89.5 188.1-187.7 14.7-128.4-85.1-237.7-210.2-239.1v-57.6c0-3.2-4-4.9-6.7-2.9l-118.6 87.1c-2 1.5-2 4.4 0 5.9l118.6 87.1c2.7 2 6.7.2 6.7-2.9v-57.5c87.9 1.4 158.3 76.2 152.3 165.6-5.1 76.9-67.8 139.3-144.7 144.2-81.5 5.2-150.8-53-163.2-130-2.3-14.3-14.8-24.7-29.2-24.7-17.9 0-31.9 15.9-29.1 33.6 17.4 109.7 118.7 192 236 178.9z"
                    />
                </svg>
            </button>
            <slot name="extra-controls-after" />
        </div>
        <div class="player__progress">
            <div
                ref="bar"
                class="player-progress"
                @mouseenter="onBarMouseEnter"
                @mousemove="onBarMouseMove"
                @mouseleave="onBarMouseLeave"
                @click="seek"
            >
                <div ref="progress" class="player-progress__bar" />
                <div ref="loaded" class="player-progress__loaded" />
                <button ref="handle" class="player-progress__handle" />
            </div>
        </div>
        <div class="player__duration">{{ seekTime || timeString }} / {{ durationString }}</div>
        <div v-if="!hideRate" class="player__rate-controls">
            <VDropdown placement="top">
                <button type="button" class="button button--badge player__rate">{{ rate }}x</button>
                <template #popper>
                    <div class="player__rate-list">
                        <button
                            v-for="r in playbackRates"
                            :key="r"
                            v-close-popper
                            type="button"
                            class="player__rate-button button button--badge"
                            @click="setRate(r)"
                        >
                            {{ r }}x
                        </button>
                    </div>
                </template>
            </VDropdown>
        </div>
        <div v-if="!hideVolume" class="player__rate-controls">
            <VDropdown placement="top">
                <button type="button" class="button button--badge player__rate">
                    <svg
                        v-if="volume > 0"
                        class="player__volume-icon"
                        width="9"
                        height="9"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448.019 448.019"
                    >
                        <path
                            fill="#868686"
                            d="M294.953 1.587c-5.568-2.656-12.128-1.952-16.928 1.92L122.377 128.019H48.009c-17.632 0-32 14.368-32 32v128c0 17.664 14.368 32 32 32h74.368l155.616 124.512a16.158 16.158 0 0010.016 3.488c2.368 0 4.736-.544 6.944-1.6a15.968 15.968 0 009.056-14.4v-416a16.05 16.05 0 00-9.056-14.432zM385.001 110.867c-6.304-6.208-16.416-6.112-22.624.128-6.208 6.304-6.144 16.416.128 22.656 24.192 23.872 37.504 55.968 37.504 90.368s-13.312 66.496-37.504 90.368c-6.272 6.176-6.336 16.32-.128 22.624a15.943 15.943 0 0011.36 4.736c4.064 0 8.128-1.536 11.264-4.64 30.336-29.856 47.008-70.048 47.008-113.088s-16.672-83.232-47.008-113.152z"
                        />
                    </svg>
                    <svg
                        v-else
                        class="player__volume-icon"
                        height="9"
                        width="9"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 448.075 448.075"
                    >
                        <path
                            fill="#868686"
                            d="M352.021 16.075c0-6.08-3.52-11.84-8.96-14.4-5.76-2.88-12.16-1.92-16.96 1.92l-141.76 112.96 167.68 167.68V16.075zM443.349 420.747l-416-416c-6.24-6.24-16.384-6.24-22.624 0s-6.24 16.384 0 22.624l100.672 100.704h-9.376c-9.92 0-18.56 4.48-24.32 11.52-4.8 5.44-7.68 12.8-7.68 20.48v128c0 17.6 14.4 32 32 32h74.24l155.84 124.48c2.88 2.24 6.4 3.52 9.92 3.52 2.24 0 4.8-.64 7.04-1.6 5.44-2.56 8.96-8.32 8.96-14.4v-57.376l68.672 68.672c3.136 3.136 7.232 4.704 11.328 4.704s8.192-1.568 11.328-4.672c6.24-6.272 6.24-16.384 0-22.656z"
                        />
                    </svg>
                </button>
                <template #popper>
                    <div class="player__volume">
                        <input
                            v-model.number="volume"
                            class="player__volume-range"
                            max="1"
                            min="0"
                            step="0.05"
                            type="range"
                        />
                    </div>
                </template>
            </VDropdown>
        </div>
        <audio ref="audio" class="player__audio" :src="src" controls />
    </div>
</template>

<script>
import playerMixin from './playerMixin';

export default {
    name: 'Player',
    mixins: [playerMixin],
};
</script>

<style lang="scss" src="./Player.scss"></style>
